import { router } from '@inertiajs/react';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { ApplicationReviewer } from '@/types';

interface ReviewersTableProps {
  eventSlug: string;
  reviewers: ApplicationReviewer[];
}

export function ReviewersTable({ eventSlug, reviewers }: ReviewersTableProps) {
  const [isPending, setIsPending] = useState(false);

  const handleRemove = (adminId: string) => {
    router.delete(`/admin/${eventSlug}/reviewers/${adminId}`, {
      preserveScroll: true,
      onStart: () => setIsPending(true),
      onFinish: () => setIsPending(false),
    });
  };

  if (reviewers.length === 0) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        <p>No reviewers configured for this event yet.</p>
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[250px]">Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Role</TableHead>
            <TableHead className="w-[100px] text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {reviewers.map((reviewer) => (
            <TableRow key={reviewer.id}>
              <TableCell className="font-medium">
                {reviewer.admin.fullName}
              </TableCell>
              <TableCell>{reviewer.admin.email}</TableCell>
              <TableCell>
                <Badge variant="outline">
                  {reviewer.admin.role === 'full' ? 'Full Access' : 'Guest'}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  disabled={isPending}
                  onClick={() => handleRemove(reviewer.adminId)}
                >
                  <Trash2 className="h-4 w-4 text-red-600" />
                  <span className="sr-only">Remove reviewer</span>
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
